import { environment } from '../environments/environment';
import { IAppConfig } from '../utils/AppConfig';
import { EFilterName } from './IconService';
import { cats } from '../utils/setup';

/**
 * Service to load and manage client-specific configuration
 * Loads JSON config file based on environment settings
 */
class AppConfigService {
    private _config: IAppConfig | null = null;
    private _loaded: boolean = false;
    private _loadPromise: Promise<IAppConfig> | null = null;

    /**
     * Load configuration from JSON file
     * Should be called before app initialization
     */
    async load(): Promise<IAppConfig> {
        if (this._loaded && this._config) {
            return this._config;
        }

        if (this._loadPromise) {
            return this._loadPromise;
        }

        this._loadPromise = (async () => {
            try {
                const response = await fetch(environment.configUrl);
                if (!response.ok) {
                    throw new Error(`Failed to load config: ${response.statusText}`);
                }
                
                const configData = await response.json();
                
                // Convert filterName string to enum if needed
                let filterName = EFilterName.beliggenhedskommune;
                if (configData.filterName) {
                    if (typeof configData.filterName === 'string') {
                        filterName = EFilterName[configData.filterName as keyof typeof EFilterName] || EFilterName.beliggenhedskommune;
                    } else {
                        filterName = configData.filterName;
                    }
                }
                
                // Merge with categories from setup (categories are complex and should come from code)
                this._config = {
                    ...configData,
                    filterName: filterName,
                    categories: cats, // Always use categories from setup.ts
                } as IAppConfig;
                
                this._loaded = true;
                
                console.log(`✅ Loaded config for client: ${environment.client}`);
                return this._config;
            } catch (error) {
                console.error('❌ Failed to load app config:', error);
                // Fallback to default config if available
                throw error;
            }
        })();

        return this._loadPromise;
    }

    /**
     * Get the loaded configuration
     * Returns null if not loaded yet
     */
    get config(): IAppConfig | null {
        return this._config;
    }

    /**
     * Check if config is loaded
     */
    get isLoaded(): boolean {
        return this._loaded;
    }

    /**
     * Get current client identifier
     */
    get client(): string {
        return environment.client;
    }
}

// Export singleton instance
export const appConfigService = new AppConfigService();

